import './view-matched-candidate.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BackArrowIcon from 'assets/images/iPrise/BackArrow.svg';
import { useDebounce } from 'hooks';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useFetchList,
  useFetchSingleData,
} from 'services/common/react-query-common-service';
import { SALES_ENDPOINT } from 'utils/Constant';

import CandidateMatchScoresScreen from '../candidate-match-scores';
import StylishLoader from '../components/StylishLoader';

const ViewMatchedCandidate: React.FC = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const jobId = state?.Params?.jobId || '';
  const score = String(state?.Params?.score || '');
  const tableType = state?.Params?.tableType || '';
  const workType: string | null = state?.Params?.workType || null;
  const isLocationSort = !workType || workType.toLowerCase() !== 'remote';

  const [localSearch, setLocalSearch] = useState('');
  const debouncedSearch = useDebounce(localSearch, 500);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openScoreModal, setOpenScoreModal] = useState(false);
  const [selectedScoreCandidateId, setSelectedScoreCandidateId] = useState<
    string | null
  >(null);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const { data: matchedResp, isLoading } = useFetchList<any>(
    ['matched-candidates-full', jobId, page, rowsPerPage, debouncedSearch],
    `${String(SALES_ENDPOINT.MatchedCandidates)}/${String(
      jobId,
    )}?score=${score}&type=matched&sourceQueue=jobQueue&skip=${
      page * rowsPerPage
    }&take=${rowsPerPage}&search=${String(debouncedSearch)}${
      isLocationSort ? '&sortby=location' : ''
    }`,
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  const { data: jobData } = useFetchSingleData<any>(
    ['job-posting', jobId, Number(score)],
    '', // Pass empty string to id to handle query params manually in endpoint
    'job-postings/job-queue?score=' +
      Number(score) +
      (jobId ? '&id=' + (jobId as string) : ''),
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  const candidates = useMemo(() => matchedResp?.data || [], [matchedResp]);
  const totalCount = matchedResp?.total || 0;

  const getMatchColor = (percentage: number) => {
    if (percentage >= 80) return '#EBF0FF'; // Blue
    return '#EBF0FF';
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const navigateTo = (item: any, jobId: string) => {
    // Determine the base path from current location
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';

    const candidateId = item?.id || '';

    navigate(`${basePath}/view-candidate/${String(candidateId)}`, {
      state: {
        Params: {
          candidateInfo: item,
          jobId: jobId, // Keeping item.id as jobId if needed by the page
          score,
          tableType,
        },
      },
    });
  };

  return (
    <Box className="view-matched-candidate inter-font-family">
      {/* Header Section */}
      <Box className="screen-header">
        <Box className="header-left">
          <IconButton className="back-button" onClick={handleBack}>
            <img src={BackArrowIcon} alt="Back" />
          </IconButton>
          <Typography className="screen-title">More</Typography>
        </Box>
        <Box className="header-right">
          <TextField
            placeholder="Search"
            size="small"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="search-field"
          />
        </Box>
      </Box>

      {/* Table Section */}
      <Box className="table-card">
        <Box className="table-main-container">
          <TableContainer className="candidates-table-container">
            <Table stickyHeader className="candidates-table">
              <TableHead className="table-header">
                <TableRow>
                  {[
                    'Name',
                    'Email',
                    'Phone',
                    'Location',
                    'Visa Status',
                    'Relocation Status',
                    'Pay',
                    'Match %',
                    'Status',
                  ].map((head) => (
                    <TableCell
                      key={head}
                      className="header-cell"
                      align={head === 'Status' ? 'center' : 'left'}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className="table-body">
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <StylishLoader
                        mode="table"
                        text="Loading candidates..."
                      />
                    </TableCell>
                  </TableRow>
                ) : candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography
                        sx={{
                          color: '#5e6c84',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 500,
                          pt: 5,
                        }}>
                        No candidates matched.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates.map((item: any) => {
                    const c = item.candidate;
                    const name =
                      `${String(c?.firstName || '')} ${String(
                        c?.middleName || '',
                      )} ${String(c?.lastName || '')}`.trim() || '-';
                    const location =
                      [c?.city, c?.state, c?.country]
                        .filter(Boolean)
                        .join(', ') || '-';

                    return (
                      <TableRow
                        key={item.id}
                        className="table-row"
                        onClick={() => navigateTo(item, jobId)}
                        sx={{ cursor: 'pointer' }}>
                        <TableCell className="body-cell">{name}</TableCell>
                        <TableCell className="body-cell">
                          {c?.email || '-'}
                        </TableCell>
                        <TableCell className="body-cell">
                          {c?.phone || c?.mobileNumber || '-'}
                        </TableCell>
                        <TableCell className="body-cell">{location}</TableCell>
                        <TableCell className="body-cell">
                          {c?.workStatus?.name || '-'}
                        </TableCell>
                        <TableCell className="body-cell">
                          {c?.relocationPreferred || '-'}
                        </TableCell>
                        <TableCell className="body-cell">
                          {item.payRate ? `$ ${String(item.payRate)}` : '-'}
                        </TableCell>
                        <TableCell sx={{ padding: '12px 16px' }}>
                          <Typography
                            component={'div'}
                            style={{
                              alignItems: 'center',
                              backgroundColor: getMatchColor(
                                item?.matchPercentage,
                              ),
                              border: '1px solid #7995E8',
                              borderRadius: '9999px',
                              color: '#2453C3',
                              cursor: 'pointer',
                              display: 'flex',
                              fontFamily: 'Inter',
                              fontSize: '10px',
                              fontWeight: 600,
                              justifyContent: 'center',
                              lineHeight: '14px',
                              minHeight: '20px',
                              padding: '2px 4px',
                              width: '40px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedScoreCandidateId(item?.id);
                              setOpenScoreModal(true);
                            }}>
                            {item.matchPercentage || '-'}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center" className="body-cell">
                          <CheckCircleIcon className="status-icon" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Filters Sidebar UI on the right */}
          <Box className="filters-sidebar-tab">
            <IconButton className="filter-icon-btn">
              <FilterListIcon />
            </IconButton>
            <Typography className="filter-label">Filters</Typography>
          </Box>
        </Box>

        {/* Pagination Section */}
        <TablePagination
          className="candidates-pagination"
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100, 150, 200, 250]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${String(from)} to ${String(to)} of ${String(
              count !== -1 ? count.toLocaleString() : `more than ${to}`,
            )}`
          }
        />
      </Box>

      <Dialog
        open={openScoreModal}
        onClose={() => setOpenScoreModal(false)}
        fullScreen>
        <CandidateMatchScoresScreen
          isModal={true}
          onClose={() => setOpenScoreModal(false)}
          candidates={candidates}
          expFrom={
            jobData?.data?.[0]?.expFrom ?? candidates?.[0]?.jobPost?.expFrom
          }
          expTo={jobData?.data?.[0]?.expTo ?? candidates?.[0]?.jobPost?.expTo}
          goal={jobData?.data?.[0]?.goal ?? candidates?.[0]?.jobPost?.goal}
          jobTitle={
            jobData?.data?.[0]?.jobTitle ?? candidates?.[0]?.jobPost?.jobTitle
          }
          loading={isLoading}
          selectedCandidateId={selectedScoreCandidateId || undefined}
        />
      </Dialog>
    </Box>
  );
});

export default ViewMatchedCandidate;
